<script setup lang="ts">
    import { InputRow, MultiCombobox, Select } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { useCompanyData } from '@/composables/useCompanyData';
    import { useRoleData } from '@/composables/useRoleData';
    import { computed, watch } from 'vue';
    import { Company, RoleGroup, RoleOption } from '@/types';
    import UserRoleComboBox from './UserRoleComboBox.vue';

    const { companies, isPlaceholderDataCompanies } = useCompanyData(true);
    const { roleOptions, isPlaceholderDataRoleOptions } = useRoleData();

    const props = defineProps<{
        form: any;
    }>();

    const filteredCompanies = computed(() => {
        return companies.value.filter((company: Company) => {
            if (company.deleted_at === null) {
                return true;
            }

            return props.form.company_ids.includes(String(company.id));
        });
    });

    const selectedCompanies = computed(() => {
        return filteredCompanies.value.filter((company: Company) =>
            props.form.company_ids.includes(String(company.id)),
        );
    });

    const filteredRoles = computed(() => {
        return roleOptions.value.filter((group: RoleGroup) =>
            props.form.company_ids.includes(String(group.id)),
        );
    });

    watch(
        () => props.form.company_ids,
        (newCompanyIds, oldCompanyIds) => {
            if (oldCompanyIds) {
                props.form.contact_persons = props.form.contact_persons.filter(
                    (contactPersonId: string | number) =>
                        newCompanyIds.includes(String(contactPersonId)),
                );
                props.form.role_ids = props.form.role_ids.filter((role: RoleOption) =>
                    newCompanyIds.includes(String(role.company_id)),
                );
            }
        },
        { deep: true },
    );
</script>

<template>
    <div class="grid gap-3 sm:grid-cols-1">
        <InputRow
            :form="form"
            name="name"
            label="users.form.name"
        >
            <Input
                id="name"
                v-model="form.name"
                :placeholder="$t('users.form.name')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="email"
            label="users.form.email"
        >
            <Input
                id="email"
                v-model="form.email"
                :placeholder="$t('users.form.email')"
                type="email"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="phone"
            label="users.form.phone"
        >
            <Input
                id="phone"
                v-model="form.phone"
                :placeholder="$t('users.form.phone')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="company_ids"
            label="users.form.company_ids"
        >
            <MultiCombobox
                v-model="form.company_ids"
                :options="filteredCompanies"
                :placeholder="$t('users.form.company_ids')"
                label-by="name"
                value-by="id"
                :loading="isPlaceholderDataCompanies"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="role_ids"
            label="users.form.role_id"
        >
            <UserRoleComboBox
                v-model="form.role_ids"
                :options="filteredRoles"
                grouped
                :placeholder="$t('users.form.role_id')"
                :loading="isPlaceholderDataRoleOptions"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="contact_persons"
            label="users.form.contact_persons"
        >
            <MultiCombobox
                v-model="form.contact_persons"
                :options="selectedCompanies"
                :placeholder="$t('users.form.contact_persons')"
                label-by="name"
                value-by="id"
                :loading="isPlaceholderDataCompanies"
            />
        </InputRow>

        <InputRow
            :form="form"
            name="language"
            label="users.form.language"
        >
            <Select
                v-model="form.language"
                translate
                :options="[
                    { value: 'da', label: 'users.language.da' },
                    { value: 'en', label: 'users.language.en' },
                ]"
            />
        </InputRow>
    </div>
</template>
